import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';

interface NavigationElement {
  id: string;
  onBack: () => void;
}

interface NavigationContextType {
  navigationStack: NavigationElement[];
  pushToStack: (element: NavigationElement) => void;
  popFromStack: () => void;
  updateStack: (id: string, newElement: NavigationElement) => void;
  deleteFromStack: (id: string) => void;
  removeFromStack: (id: string) => void; // Remove without navigation
}

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined,
);

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [navigationStack, setNavigationStack] = useState<NavigationElement[]>(
    [],
  );
  const [backNavigationCounter, setBackNavigationCounter] = useState<number>(0);
  const navigate = useNavigate();
  const locksRef = useRef(new Set<string>());

  const isLocked = (id: string) => locksRef.current.has(id);
  const lock = (id: string) => locksRef.current.add(id);
  const unlock = (id: string) => locksRef.current.delete(id);

  useEffect(() => {
    const handlePopState = () => {
      if (backNavigationCounter > 0) {
        setBackNavigationCounter(count => count - 1);
      } else if (navigationStack.length > 0) {
        const topElement = navigationStack[navigationStack.length - 1];
        topElement.onBack();
        setNavigationStack(currentStack => currentStack.slice(0, -1));
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [backNavigationCounter, navigationStack]);

  const pushToStack = (element: NavigationElement) => {
    const newPath =
      window.location.pathname + window.location.search + window.location.hash;
    navigate(newPath, { state: { modal: true } });
    setNavigationStack(prevStack => [...prevStack, element]);
  };

  const popFromStack = () => {
    setNavigationStack(prevStack => {
      if (prevStack.length > 0) {
        return prevStack.slice(0, -1);
      }
      return prevStack;
    });
    setBackNavigationCounter(count => count + 1);
    navigate(-1);
  };

  const updateStack = (id: string, newElement: NavigationElement) => {
    setNavigationStack(prevStack => {
      const index = prevStack.findIndex(el => el.id === id);
      if (index !== -1) {
        const newStack = [...prevStack];
        newStack[index] = newElement;
        return newStack;
      }
      return prevStack;
    });
  };

  const deleteFromStack = (id: string) => {
    if (isLocked(id)) return;
    lock(id);

    setNavigationStack(currentStack => {
      const newStack = currentStack.filter(el => el.id !== id);
      const numRemoved = currentStack.length - newStack.length;

      if (numRemoved === 0) {
        unlock(id);
        return currentStack;
      }

      // Schedule navigation and counter update outside of setState
      setTimeout(() => {
        setBackNavigationCounter(count => count + numRemoved);
        for (let i = 0; i < numRemoved; i++) {
          setTimeout(() => {
            navigate(-1);
            if (i === numRemoved - 1) {
              unlock(id);
            }
          }, 50 * i);
        }
      }, 0);

      return newStack;
    });
  };

  // Remove from stack without triggering navigation (for programmatic closes)
  const removeFromStack = (id: string) => {
    setNavigationStack(currentStack => {
      const newStack = currentStack.filter(el => el.id !== id);
      const numRemoved = currentStack.length - newStack.length;

      if (numRemoved > 0) {
        // Increment counter to ignore the next popstate events from history
        setBackNavigationCounter(count => count + numRemoved);
        // Navigate back to clean up history entries added by pushToStack
        for (let i = 0; i < numRemoved; i++) {
          setTimeout(() => navigate(-1), 50 * i);
        }
      }

      return newStack;
    });
  };

  return (
    <NavigationContext.Provider
      value={{
        navigationStack,
        pushToStack,
        popFromStack,
        updateStack,
        deleteFromStack,
        removeFromStack,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};
